import React, { useState, useRef, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Tooltip,
  App,
  Select,
  Tag,
} from 'antd';
import {
  SearchOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import {
  useGetFacturasQuery,
  useTimbrarFacturaMutation,
  useCancelarFacturaMutation,
  useDeleteFacturaMutation,
  useGetFacturacionConfigQuery,
} from '../../services/facturacionApi';
import { FacturaListItem, FacturaFilter } from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getApiBaseUrl } from '../../config/api';
import { getErrorMessage } from '../../utils/errorHelpers';
import FacturaDetalleModal from './FacturaDetalleModal';
import CancelarFacturaModal, { type CancelarFacturaFormValues } from './CancelarFacturaModal';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';
import { useAppSelector } from '../../hooks/redux';
import { canManageFacturas, canOperateFacturas, isSupportUser } from '../../utils/roleHelpers';
import { estadoVisibleFactura, ETIQUETA_ESTADO_FACTURA } from '../../utils/facturaEstado';

const ESTADOS = ['Borrador', 'Timbrada', 'Cancelada'] as const;

const FacturasManager: React.FC = () => {
  const { isMobile, isTablet } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message, modal } = App.useApp();
  const [filters, setFilters] = useState<FacturaFilter>({
    pageNumber: 1,
    pageSize: isMobile ? 10 : 20,
  });
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedFacturaId, setSelectedFacturaId] = useState<number | null>(null);
  const [detalleModalVisible, setDetalleModalVisible] = useState(false);
  const [facturaACancelar, setFacturaACancelar] = useState<FacturaListItem | null>(null);

  // API hooks
  const { data: facturasData, isLoading, refetch } = useGetFacturasQuery(filters);
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const { user } = useAppSelector((state) => state.auth);
  const esSoporte = isSupportUser(user?.email);
  // Esconder no es la barrera (el backend exige el rol); evita toparse con un 403.
  const puedeGestionar = canManageFacturas(user?.role, esSoporte);
  const puedeOperar = canOperateFacturas(user?.role, esSoporte);
  /** Timbrar o borrar una global (muchas órdenes) es de administración; la de una orden, de caja. */
  const puedeOperarEsta = (f: FacturaListItem) =>
    f.esGlobal || (f.ordenesCubiertas ?? 1) > 1 ? puedeGestionar : puedeOperar;
  /** Cancelar ante el SAT: timbrada, sin otra solicitud en curso, y con permiso. */
  const puedeCancelar = (f: FacturaListItem) => {
    const visible = estadoVisibleFactura(f.estado, f.estatusCancelacion);
    return puedeGestionar && !!f.uuid && (visible === 'Timbrada' || visible === 'CancelacionRechazada');
  };
  const [timbrarFactura, { isLoading: timbrandoFactura }] = useTimbrarFacturaMutation();
  const [cancelarFactura, { isLoading: cancelandoFactura }] = useCancelarFacturaMutation();
  const [deleteFactura, { isLoading: deletingFactura }] = useDeleteFacturaMutation();

  // Debounced search using useRef and useEffect
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: value || undefined, pageNumber: 1 }));
    }, 500);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setFilters(prev => ({ ...prev, searchTerm: undefined, pageNumber: 1 }));
  };

  // Auto-filter by serie when config loads (only once on mount)
  const hasSetInitialSerie = useRef(false);
  useEffect(() => {
    if (facturacionConfig?.serie && !filters.serie && !hasSetInitialSerie.current) {
      setFilters(prev => ({ ...prev, serie: facturacionConfig.serie }));
      hasSetInitialSerie.current = true;
    }
  }, [facturacionConfig, filters.serie]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleEstadoChange = (estado: string | undefined) => {
    setFilters({ ...filters, estado: estado || undefined, pageNumber: 1 });
  };

  const handleSerieChange = (serie: string | undefined) => {
    setFilters({ ...filters, serie: serie || undefined, pageNumber: 1 });
  };

  const handleOrigenChange = (origen: 'Local' | 'Autofactura' | undefined) => {
    setFilters({ ...filters, origen: origen || undefined, pageNumber: 1 });
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({
        ...filters,
        fechaInicio: dates[0].format('YYYY-MM-DD'),
        fechaFin: dates[1].format('YYYY-MM-DD'),
        pageNumber: 1,
      });
    } else {
      setFilters({
        ...filters,
        fechaInicio: undefined,
        fechaFin: undefined,
        pageNumber: 1,
      });
    }
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setFilters({ ...filters, pageNumber: page, pageSize: pageSize || filters.pageSize });
  };

  const handleVerDetalle = (facturaId: number) => {
    setSelectedFacturaId(facturaId);
    setDetalleModalVisible(true);
  };

  const handleDownloadPDF = async (factura: FacturaListItem) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/facturacion/pdf/${factura.facturaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al descargar PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${factura.serie}-${factura.folio}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      message.error('Error al descargar el PDF');
    }
  };

  const handleDownloadXML = async (factura: FacturaListItem) => {
    if (!factura.uuid) {
      message.warning('Solo se puede descargar XML de facturas timbradas');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/facturacion/xml/${factura.facturaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al descargar XML: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${factura.serie}-${factura.folio}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading XML:', err);
      message.error('Error al descargar el XML');
    }
  };

  const handleTimbrarFactura = async (factura: FacturaListItem) => {
    if (factura.uuid) {
      message.info('Esta factura ya está timbrada');
      return;
    }

    try {
      await timbrarFactura(factura.facturaId).unwrap();
      message.success(`Factura ${factura.folio} timbrada exitosamente`);
    } catch (error: unknown) {
      console.error('Error al timbrar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al timbrar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleCancelarFactura = (factura: FacturaListItem) => {
    if (!factura.uuid) {
      message.warning('Solo se pueden cancelar facturas timbradas');
      return;
    }

    if (factura.estado === 'Cancelada') {
      message.info('Esta factura ya está cancelada');
      return;
    }

    // El motivo se pregunta: iba fijo en '02' sin consultar al usuario, y el UUID de
    // sustitución —que el motivo 01 exige— nunca se llenaba.
    setFacturaACancelar(factura);
  };

  const confirmarCancelacion = async (values: CancelarFacturaFormValues) => {
    if (!facturaACancelar) return;

    try {
      const resultado = await cancelarFactura({
        facturaId: facturaACancelar.facturaId,
        data: { motivo: values.motivo, uuidSustitucion: values.uuidSustitucion },
      }).unwrap();
      // "Solicitada" no es "cancelada": si el receptor debe aceptarla, el SAT tarda.
      if (resultado.confirmada) message.success(resultado.mensaje);
      else message.info(resultado.mensaje, 8);
      setFacturaACancelar(null);
    } catch (error: unknown) {
      console.error('Error al cancelar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al cancelar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleDeleteFactura = (factura: FacturaListItem) => {
    // Solo se pueden borrar facturas en borrador
    if (factura.estado !== 'Borrador') {
      message.warning('Solo se pueden eliminar facturas en estado Borrador');
      return;
    }

    modal.confirm({
      title: '¿Eliminar factura?',
      content: `¿Está seguro de que desea eliminar la factura ${factura.folio}? Esta acción es irreversible.`,
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteFactura(factura.facturaId).unwrap();
          message.success('Factura eliminada exitosamente');
        } catch (error: unknown) {
          console.error('Error al eliminar factura:', error);
          const errorMsg = getErrorMessage(error, 'Error al eliminar la factura');
          message.error(errorMsg, 8);
        }
      },
    });
  };

  const estadoPill = (factura: FacturaListItem) => {
    // Una cancelación solicitada no es una cancelación: el SAT puede tardar 3 días hábiles
    // o el receptor rechazarla, y mientras tanto la factura sigue vigente.
    const visible = estadoVisibleFactura(factura.estado, factura.estatusCancelacion);
    const cls =
      visible === 'Timbrada' || visible === 'CancelacionRechazada' ? ' fac-estado--timbrada' :
      visible === 'Cancelada' ? ' fac-estado--cancelada' : '';
    return (
      <span className={`fac-estado${cls}`}>
        {ETIQUETA_ESTADO_FACTURA[visible].texto}
        {factura.esGlobal ? ' · Global' : ''}
        {(factura.ticketsPorSacar ?? 0) > 0 ? ` · ${factura.ticketsPorSacar} por sacar` : ''}
      </span>
    );
  };

  // Mobile-optimized columns
  const getMobileColumns = (): ColumnsType<FacturaListItem> => [
    {
      title: 'Factura',
      key: 'factura',
      render: (_, record) => (
        <div style={{ padding: '4px 0' }}>
          {/* Info row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{record.serie}-{record.folio}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {formatInTimeZone(record.fecha, 'DD/MM/YY')}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="fac-money" style={{ fontSize: 14 }}>${record.total.toFixed(2)}</div>
              {estadoPill(record)}
            </div>
          </div>
          {/* Cliente */}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {record.clienteRazonSocial}
          </div>
          {/* Origen */}
          {record.origen && (
            <div style={{ marginTop: 4 }}>
              <Tag color={record.origen === 'Autofactura' ? 'purple' : 'blue'}>{record.origen}</Tag>
              {record.origen === 'Local' && record.generadaPor && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.generadaPor}</span>
              )}
            </div>
          )}
          {/* Actions row */}
          <div style={{ marginTop: 8, display: 'flex', gap: 4, justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: 8, flexWrap: 'wrap' }}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => { e.stopPropagation(); handleVerDetalle(record.facturaId); }}
            >
              Ver
            </Button>
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={(e) => { e.stopPropagation(); handleDownloadPDF(record); }}
            >
              PDF
            </Button>
            {record.uuid && (
              <Button
                type="text"
                size="small"
                icon={<FileZipOutlined />}
                onClick={(e) => { e.stopPropagation(); handleDownloadXML(record); }}
              >
                XML
              </Button>
            )}
            {puedeOperarEsta(record) && !record.uuid && record.estado !== 'Cancelada' && (
              <Button
                type="primary"
                size="small"
                icon={<SafetyCertificateOutlined />}
                onClick={(e) => { e.stopPropagation(); handleTimbrarFactura(record); }}
                loading={timbrandoFactura}
              >
                Timbrar
              </Button>
            )}
            {puedeOperarEsta(record) && record.estado === 'Borrador' && (
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); handleDeleteFactura(record); }}
                loading={deletingFactura}
              >
                Eliminar
              </Button>
            )}
            {puedeCancelar(record) && (
              <Button
                type="text"
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                onClick={(e) => { e.stopPropagation(); handleCancelarFactura(record); }}
                loading={cancelandoFactura}
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>
      ),
    },
  ];

  // Desktop columns
  const getDesktopColumns = (): ColumnsType<FacturaListItem> => [
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 130,
      render: (folio, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.serie}-{folio}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {formatInTimeZone(record.fecha, 'DD/MM/YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Cliente',
      key: 'cliente',
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.clienteRazonSocial}</div>
          <div className="fac-mono" style={{ color: 'var(--text-secondary)' }}>{record.clienteRFC}</div>
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total) => <span className="fac-money">${total.toFixed(2)}</span>,
      align: 'right',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 110,
      render: (_estado, record) => estadoPill(record),
    },
    ...(!isTablet ? [{
      title: 'Generada por',
      key: 'origen',
      width: 150,
      render: (_: unknown, record: FacturaListItem) => (
        record.origen ? (
          <div>
            <Tag color={record.origen === 'Autofactura' ? 'purple' : 'blue'}>{record.origen}</Tag>
            {record.origen === 'Local' && record.generadaPor && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.generadaPor}</div>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--text-secondary)' }}>—</span>
        )
      ),
    }] : []),
    ...(!isTablet ? [{
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 150,
      render: (uuid: string) => (
        uuid ? (
          <Tooltip title={uuid}>
            <span className="fac-mono" style={{ color: 'var(--text-secondary)' }}>
              {uuid.substring(0, 12)}…
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: 'var(--text-secondary)' }}>—</span>
        )
      ),
    }] : []),
    {
      title: '',
      key: 'actions',
      fixed: 'right' as const,
      width: 220,
      align: 'right' as const,
      render: (_, record) => (
        <Space size={2}>
          {/* La acción pendiente lleva texto; el resto son íconos discretos */}
          {puedeOperarEsta(record) && !record.uuid && record.estado !== 'Cancelada' && (
            <Button
              type="link"
              size="small"
              icon={<SafetyCertificateOutlined />}
              onClick={() => handleTimbrarFactura(record)}
              loading={timbrandoFactura}
            >
              Timbrar
            </Button>
          )}
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record.facturaId)}
            />
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPDF(record)}
            />
          </Tooltip>
          {record.uuid && (
            <Tooltip title="Descargar XML">
              <Button
                type="text"
                size="small"
                icon={<FileZipOutlined />}
                onClick={() => handleDownloadXML(record)}
              />
            </Tooltip>
          )}
          {puedeOperarEsta(record) && record.estado === 'Borrador' && (
            <Tooltip title="Eliminar factura">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteFactura(record)}
                loading={deletingFactura}
              />
            </Tooltip>
          )}
          {puedeCancelar(record) && (
            <Tooltip title="Cancelar factura">
              <Button
                type="text"
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleCancelarFactura(record)}
                loading={cancelandoFactura}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const columns = isMobile ? getMobileColumns() : getDesktopColumns();

  return (
    <div>
      {/* Filtros: estado como pills + búsqueda + serie + rango, una sola fila */}
      <div className="fac-filters">
        <div className="fac-pills">
          <button
            type="button"
            className={`fac-pill${!filters.estado ? ' fac-pill--selected' : ''}`}
            onClick={() => handleEstadoChange(undefined)}
          >
            Todas
          </button>
          {ESTADOS.map((estado) => (
            <button
              key={estado}
              type="button"
              className={`fac-pill${filters.estado === estado ? ' fac-pill--selected' : ''}`}
              onClick={() => handleEstadoChange(filters.estado === estado ? undefined : estado)}
            >
              {estado}
            </button>
          ))}
        </div>
        <Input
          placeholder={isMobile ? 'Buscar…' : 'RFC, razón social o folio'}
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          allowClear
          style={{ width: isMobile ? '100%' : 230 }}
        />
        <Input
          placeholder="Serie"
          value={filters.serie}
          onChange={(e) => handleSerieChange(e.target.value || undefined)}
          allowClear
          style={{ width: 90 }}
        />
        <Select
          placeholder="Origen"
          value={filters.origen}
          onChange={handleOrigenChange}
          allowClear
          style={{ width: 130 }}
          options={[
            { value: 'Local', label: 'Local' },
            { value: 'Autofactura', label: 'Autofactura' },
          ]}
        />
        <MobileDateRangePicker
          onChange={handleDateRangeChange}
          placeholder={['Inicio', 'Fin']}
          value={
            filters.fechaInicio && filters.fechaFin
              ? [dayjs(filters.fechaInicio), dayjs(filters.fechaFin)]
              : null
          }
        />
        <div style={{ flex: 1 }} />
        <Tooltip title="Actualizar">
          <Button type="text" icon={<ReloadOutlined />} onClick={() => refetch()} />
        </Tooltip>
      </div>

      {/* Tabla */}
      <div className="fac-panel">
        <Table
          columns={columns}
          dataSource={facturasData?.items || []}
          loading={isLoading}
          rowKey="facturaId"
          rowClassName={(record) => (record.estado === 'Borrador' ? 'fac-row--draft' : '')}
          scroll={isMobile ? undefined : { x: 900 }}
          size={isMobile ? 'small' : 'middle'}
          pagination={{
            current: facturasData?.pageNumber || 1,
            pageSize: facturasData?.pageSize || (isMobile ? 10 : 20),
            total: facturasData?.totalCount || 0,
            showSizeChanger: !isMobile,
            showTotal: isMobile ? undefined : (total) => `Total ${total} facturas`,
            onChange: handlePageChange,
            pageSizeOptions: ['10', '20', '50', '100'],
            size: isMobile ? 'small' : 'default',
            simple: isMobile,
          }}
        />
      </div>

      {/* Modal de Detalle */}
      {selectedFacturaId && (
        <FacturaDetalleModal
          facturaId={selectedFacturaId}
          visible={detalleModalVisible}
          onClose={() => {
            setDetalleModalVisible(false);
            setSelectedFacturaId(null);
            refetch(); // Refrescar lista de facturas
          }}
          onFacturaRegenerated={(newFacturaId) => {
            // Cuando la factura se regenera, abrir la nueva factura
            setSelectedFacturaId(newFacturaId);
            setDetalleModalVisible(true);
            refetch(); // Refrescar lista de facturas
          }}
        />
      )}

      <CancelarFacturaModal
        open={!!facturaACancelar}
        folio={facturaACancelar ? `${facturaACancelar.serie}-${facturaACancelar.folio}` : undefined}
        esGlobal={!!facturaACancelar?.esGlobal}
        // Sin esto el botón sigue habilitado durante la mutación y cada clic manda otra
        // cancelación al SAT.
        confirmLoading={cancelandoFactura}
        onCancel={() => setFacturaACancelar(null)}
        onConfirm={confirmarCancelacion}
      />
    </div>
  );
};

export default FacturasManager;
